// MeshAgent's remote desktop protocol, shared by the console's viewer (which encodes input and decodes the screen) and
// the API's relay (which only passes on what a session may send). Every command is a big-endian header, command (2)
// and total length (4 bytes or more) (2), then its data; one WebSocket frame may hold several. Based on MeshCentral's
// public/scripts/agent-desktop-0.0.2.js (Apache-2.0). Runs in browsers and Node: no Node-only APIs.

export const Command = {
  KEY: 1, MOUSE: 2, TILE: 3, SETTINGS: 5, REFRESH: 6, SCREEN_SIZE: 7, PAUSE: 8, CTRL_ALT_DEL: 10, DISPLAYS: 11,
  SET_DISPLAY: 12, INIT_TOUCH: 14, TOUCH: 15, MESSAGE: 17, KEY_STATE: 18, JUMBO: 27, ALERT: 65, DISPLAY_LOCATIONS: 82,
  KEY_UNICODE: 85, INPUT_LOCK: 87, CURSOR: 88,
} as const;

/** Exact lengths of what a browser may send; touch varies. Anything else is dropped by the relay. */
const VIEW_COMMANDS = new Map<number, number | null>([
  [Command.SETTINGS, 10], [Command.REFRESH, 4], [Command.PAUSE, 5], [Command.DISPLAYS, 4], [Command.SET_DISPLAY, 6], [Command.INIT_TOUCH, 4],
]);
const INPUT_COMMANDS = new Map<number, number | null>([
  [Command.KEY, 6], [Command.MOUSE, null], [Command.CTRL_ALT_DEL, 4], [Command.TOUCH, null], [Command.KEY_UNICODE, 7], [Command.INPUT_LOCK, 5],
]);
export const MAX_CLIENT_FRAME = 4096;

/**
 * Whether a frame from a viewer's browser may go to the device: every command in it is one a browser sends, of
 * the right length, and, for a view-only session, none of them is keyboard, mouse or touch input (asking whether the
 * device's own input is locked is allowed; locking or unlocking it is not). Customer sessions also use the explicit allowlist below.
 */
// Customer control is ordinary desktop input and screen streaming only. Never inherit new staff commands by default.
const CUSTOMER_COMMANDS: ReadonlySet<number> = new Set([Command.KEY,Command.KEY_UNICODE,Command.MOUSE,Command.TOUCH,
  Command.SETTINGS,Command.REFRESH,Command.PAUSE,Command.DISPLAYS,Command.INIT_TOUCH]);
export function clientFrameAllowed(frame: Uint8Array, control: boolean, customer = false): boolean {
  if (frame.length < 4 || frame.length > MAX_CLIENT_FRAME) return false;
  const view = new DataView(frame.buffer, frame.byteOffset, frame.byteLength);
  for (let at = 0; at < frame.length;) {
    if (frame.length - at < 4) return false;
    const command = view.getUint16(at), length = view.getUint16(at + 2);
    if (length < 4 || at + length > frame.length) return false;
    if (customer && !CUSTOMER_COMMANDS.has(command)) return false;
    const expected = VIEW_COMMANDS.has(command) ? VIEW_COMMANDS.get(command)
      : control || (command === Command.INPUT_LOCK && length === 5 && frame[at + 4] === 2) ? INPUT_COMMANDS.get(command) : undefined;
    if (expected === undefined) return false;
    if (expected === null ? !validVariable(command, length) : length !== expected) return false;
    at += length;
  }
  return true;
}
/** The display a viewer's frame (one clientFrameAllowed accepted) asks for with SET_DISPLAY, or null if it doesn't. */
export function displayAskedFor(frame: Uint8Array): number | null {
  const view = new DataView(frame.buffer, frame.byteOffset, frame.byteLength);
  for (let at = 0; at + 4 <= frame.length;) {
    const command = view.getUint16(at), length = view.getUint16(at + 2);
    if (length < 4) return null;
    if (command === Command.SET_DISPLAY && length === 6 && at + 6 <= frame.length) return view.getUint16(at + 4);
    at += length;
  }
  return null;
}

/** Whether a frame (one clientFrameAllowed accepted) holds the given command. */
export function frameHas(frame: Uint8Array, command: number): boolean {
  const view = new DataView(frame.buffer, frame.byteOffset, frame.byteLength);
  for (let at = 0; at + 4 <= frame.length;) {
    if (view.getUint16(at) === command) return true;
    const length = view.getUint16(at + 2);
    if (length < 4) return false;
    at += length;
  }
  return false;
}
const validVariable = (command: number, length: number) =>
  command === Command.MOUSE ? length === 10 || length === 12 : command === Command.TOUCH ? length >= 5 && length <= 4 + 1 + 10 * 9 : false;

// Encoders for the viewer.
const frame = (command: number, ...data: number[]) => {
  const bytes = new Uint8Array(4 + data.length);
  const view = new DataView(bytes.buffer);
  view.setUint16(0, command);
  view.setUint16(2, bytes.length);
  bytes.set(data, 4);
  return bytes;
};
const u16 = (value: number) => [(value >> 8) & 0xff, value & 0xff];
export const KeyAction = { DOWN: 1, UP: 2 } as const;
export const MouseButton = { NONE: 0, LEFT: 0x02, RIGHT: 0x08, MIDDLE: 0x20 } as const;
/** A key by Windows virtual key code. `extended` marks the right-hand Ctrl/Alt, arrows, Insert, Delete, Home, End, Page keys. */
export const keyFrame = (action: 1 | 2, keyCode: number, extended = false) =>
  frame(Command.KEY, extended ? (action === KeyAction.UP ? 3 : 4) : action - 1, keyCode & 0xff);
/** A typed character, which arrives as that character whatever the device's keyboard layout. */
export const unicodeFrame = (action: 1 | 2, codeUnit: number) => frame(Command.KEY_UNICODE, action - 1, ...u16(codeUnit));
/** Mouse at desktop coordinates: a button going down or up, or a move with no button. */
export const mouseFrame = (x: number, y: number, button: number, down: boolean) =>
  frame(Command.MOUSE, 0, down ? button : (button * 2) & 0xff, ...u16(x), ...u16(y));
export const doubleClickFrame = (x: number, y: number) => frame(Command.MOUSE, 0, 0x88, ...u16(x), ...u16(y));
/** Wheel movement: positive scrolls up, in Windows units of 120 per notch. */
export const wheelFrame = (x: number, y: number, delta: number) => {
  const clamped = Math.max(-32768, Math.min(32767, Math.round(delta)));
  return frame(Command.MOUSE, 0, 0, ...u16(x), ...u16(y), ...u16(clamped & 0xffff));
};
export const ctrlAltDelFrame = () => frame(Command.CTRL_ALT_DEL);
export const refreshFrame = () => frame(Command.REFRESH);
export const pauseFrame = (paused: boolean) => frame(Command.PAUSE, paused ? 1 : 0);
export const displaysFrame = () => frame(Command.DISPLAYS);
export const setDisplayFrame = (display: number) => frame(Command.SET_DISPLAY, ...u16(display));
/** 0 unlocks and 1 locks the device's own keyboard and mouse; 2 asks which. */
export const inputLockFrame = (code: 0 | 1 | 2) => frame(Command.INPUT_LOCK, code);
/** JPEG quality (1-100), scaling in 1/1024ths (1024 = full size) and the minimum milliseconds between frames. */
export const settingsFrame = (quality: number, scaling: number, frameMs: number) =>
  frame(Command.SETTINGS, 1, Math.max(1, Math.min(100, quality)), ...u16(Math.max(128, Math.min(1024, scaling))), ...u16(Math.max(20, Math.min(5000, frameMs))));

export type ScreenEvent =
  | { type: 'tile'; x: number; y: number; jpeg: Uint8Array }
  | { type: 'size'; width: number; height: number }
  | { type: 'displays'; displays: number[]; selected: number }
  | { type: 'monitors'; monitors: Monitor[] }
  | { type: 'cursor'; cursor: number }
  | { type: 'input-lock'; locked: boolean }
  | { type: 'key-state'; numLock: boolean; capsLock: boolean; scrollLock: boolean }
  | { type: 'alert'; message: string };

/**
 * Where each of the device's monitors is on its desktop, in the device's pixels, numbered as in the display list.
 * Windows places the main monitor at 0,0, so the others can be at negative positions.
 */
export interface Monitor { id: number; x: number; y: number; width: number; height: number }

/**
 * Turns the device's frames back into commands. A command can be larger than one frame (a big screen tile arrives as
 * a "jumbo" command with a 24-bit length), so data is gathered until each command is complete.
 */
/**
 * A JPEG's width and height from its frame header, without decoding it (null if it has none). Tiles are checked with
 * this before the browser decodes them: a small file can declare an enormous picture.
 */
export function jpegSize(jpeg: Uint8Array): { width: number; height: number } | null {
  if (jpeg[0] !== 0xff || jpeg[1] !== 0xd8) return null;
  let at = 2;
  while (at + 9 < jpeg.length) {
    if (jpeg[at] !== 0xff) return null;
    const marker = jpeg[at + 1]!;
    if (marker === 0xff) { at += 1; continue; } // Fill byte.
    const length = (jpeg[at + 2]! << 8) | jpeg[at + 3]!;
    // Start of frame (baseline, progressive and the rest; not DHT C4, JPG C8 or DAC CC): height, then width.
    if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
      return { height: (jpeg[at + 5]! << 8) | jpeg[at + 6]!, width: (jpeg[at + 7]! << 8) | jpeg[at + 8]! };
    }
    if (length < 2) return null;
    at += 2 + length;
  }
  return null;
}

/** The most messages one screen command may arrive in (16 MiB in 1 KiB pieces). */
export const MAX_PIECES_PER_COMMAND = 16_384;
/** The most screen commands read from one message. */
export const MAX_COMMANDS_PER_MESSAGE = 10_000;
/** The most displays a device may list. */
export const MAX_DISPLAYS = 32;
/** The most of a device's text (an alert) the viewer decodes. */
export const MAX_DEVICE_TEXT_BYTES = 512;

/** The largest screen command accepted (a jumbo command's declared length): far above any real screen update. */
export const MAX_SCREEN_COMMAND = 16 * 1024 * 1024;

export class ScreenDecoder {
  /** Bytes received but not yet read, kept as they came, and how many are needed before reading is worth trying. */
  private chunks: Uint8Array[] = [];
  private buffered = 0;
  private needed = 0;

  /** Throws if the device declares a command larger than MAX_SCREEN_COMMAND; the session should end then. */
  push(data: Uint8Array): ScreenEvent[] {
    if (!data.length) return [];
    // One command arriving in more pieces than any real device splits it into isn't a screen update.
    if (this.chunks.length >= MAX_PIECES_PER_COMMAND) {
      this.chunks = []; this.buffered = 0; this.needed = 0;
      throw new RangeError('The device sent a screen update in too many pieces to show');
    }
    this.chunks.push(data);
    this.buffered += data.length;
    // A large command split over many messages is joined once, when all of it is here, not copied again at every part.
    if (this.buffered < this.needed) return [];
    const bytes = this.chunks.length === 1 ? this.chunks[0]! : new Uint8Array(this.buffered);
    if (this.chunks.length > 1) { let offset = 0; for (const chunk of this.chunks) { bytes.set(chunk, offset); offset += chunk.length; } }
    this.chunks = []; this.buffered = 0; this.needed = 0;
    const reset = () => { this.chunks = []; this.buffered = 0; this.needed = 0; };
    const events: ScreenEvent[] = [];
    let at = 0, commands = 0;
    for (;;) {
      if (bytes.length - at < 4) { this.needed = 4; break; }
      // A device sends a handful of commands at a time; thousands at once would only stall the viewer.
      if (++commands > MAX_COMMANDS_PER_MESSAGE) { reset(); throw new RangeError('The device sent more screen updates at once than can be shown'); }
      const view = new DataView(bytes.buffer, bytes.byteOffset + at, bytes.length - at);
      let command = view.getUint16(0), length = view.getUint16(2), header = 0;
      if (command === Command.JUMBO && length === 8) {
        if (bytes.length - at < 12) { this.needed = 12; break; }
        length = view.getUint32(4); command = view.getUint16(8); header = 8;
        // Collecting it would grow without bound; nothing after it can be read in step either.
        if (length > MAX_SCREEN_COMMAND) { reset(); throw new RangeError('The device sent a screen update too large to show'); }
      }
      if (length < 4) { reset(); return events; } // Out of step: drop it rather than misread the rest.
      if (bytes.length - at < header + length) { this.needed = header + length; break; }
      const body = bytes.subarray(at + header, at + header + length);
      const event = decode(command, body);
      if (event) events.push(event);
      at += header + length;
    }
    if (at < bytes.length) { this.chunks = [bytes.slice(at)]; this.buffered = bytes.length - at; } else this.needed = 0;
    return events;
  }
}

function decode(command: number, body: Uint8Array): ScreenEvent | null {
  const view = new DataView(body.buffer, body.byteOffset, body.byteLength);
  // Text from the device is shown as a notice: only its first few hundred bytes are ever decoded.
  const text = () => new TextDecoder().decode(body.subarray(4, 4 + MAX_DEVICE_TEXT_BYTES));
  switch (command) {
    case Command.TILE: return body.length > 8 ? { type: 'tile', x: view.getUint16(4), y: view.getUint16(6), jpeg: body.subarray(8) } : null;
    case Command.SCREEN_SIZE: return body.length >= 8 ? { type: 'size', width: view.getUint16(4), height: view.getUint16(6) } : null;
    case Command.DISPLAYS: {
      if (body.length < 6) return null;
      const count = view.getUint16(4);
      if (count === 0) return { type: 'displays', displays: [], selected: 0 };
      if (count > MAX_DISPLAYS) return null; // No real computer has more; a list this long would only swamp the viewer.
      if (body.length < 8 + count * 2) return null;
      const displays = Array.from({ length: count }, (_, i) => view.getUint16(6 + i * 2));
      return { type: 'displays', displays, selected: view.getUint16(6 + count * 2) };
    }
    case Command.DISPLAY_LOCATIONS: {
      // MeshAgent: ten bytes a monitor (id, x, y, width, height), positions as Windows' signed coordinates.
      const count = (body.length - 4) / 10;
      if (!Number.isInteger(count) || count < 1 || count > MAX_DISPLAYS) return null;
      const monitors = Array.from({ length: count }, (_, i) => ({ id: view.getUint16(4 + i * 10), x: view.getInt16(6 + i * 10),
        y: view.getInt16(8 + i * 10), width: view.getUint16(10 + i * 10), height: view.getUint16(12 + i * 10) }));
      return monitors.every(m => m.id > 0 && m.id !== ALL_DISPLAYS && m.width > 0 && m.height > 0) ? { type: 'monitors', monitors } : null;
    }
    case Command.CURSOR: return body.length === 5 ? { type: 'cursor', cursor: body[4]! } : null;
    case Command.INPUT_LOCK: return body.length === 5 ? { type: 'input-lock', locked: body[4] !== 0 } : null;
    case Command.KEY_STATE: return body.length === 5 ? { type: 'key-state', numLock: !!(body[4]! & 1), scrollLock: !!(body[4]! & 2), capsLock: !!(body[4]! & 4) } : null;
    case Command.ALERT: { const message = text(); return message.startsWith('.') ? null : { type: 'alert', message }; }
    default: return null;
  }
}

/** The display number MeshAgent uses for "all displays" on Windows. */
export const ALL_DISPLAYS = 65535;
/** CSS cursors in the order MeshAgent numbers them. */
export const CURSORS = ['default', 'progress', 'crosshair', 'pointer', 'help', 'text', 'no-drop', 'move', 'nesw-resize', 'ns-resize',
  'nwse-resize', 'w-resize', 'alias', 'wait', 'none', 'not-allowed', 'col-resize', 'row-resize', 'copy', 'zoom-in', 'zoom-out'] as const;
